import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PageTabDefinition {
  id: string;
  label: string;
}

interface PageTabsProps {
  tabs: PageTabDefinition[];
  activeTabId: string;
  onSelect: (tabId: string) => void;
  ariaLabel: string;
  idPrefix: string;
  children: ReactNode;
}

function buildTabId(idPrefix: string, tabId: string): string {
  return idPrefix + '-tab-' + tabId;
}

function buildPanelId(idPrefix: string): string {
  return idPrefix + '-tabpanel';
}

export function PageTabs({
  tabs,
  activeTabId,
  onSelect,
  ariaLabel,
  idPrefix,
  children,
}: PageTabsProps) {
  const panelId = buildPanelId(idPrefix);

  return (
    <>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="flex gap-1 border-b border-zooborder mb-6 overflow-x-auto"
      >
        {tabs.map(function renderTab(tab) {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={buildTabId(idPrefix, tab.id)}
              aria-selected={isActive}
              aria-controls={panelId}
              onClick={function selectTab() {
                onSelect(tab.id);
              }}
              className={cn(
                'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors',
                isActive === true
                  ? 'border-zooprimary text-zooprimary'
                  : 'border-transparent text-zoomuted hover:text-zooindigo'
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={panelId} aria-labelledby={buildTabId(idPrefix, activeTabId)}>
        {children}
      </div>
    </>
  );
}
